// ⌘K: jump to a tab, an order or a home; a question goes to Gazelle.
import { useEffect, useMemo, useRef, useState } from 'react';
import { S } from '../lib/strings';
import { statusPill } from '../lib/orders';
import type { HomeRow, OpsOrder } from '../lib/types';
import type { Source } from '../lib/api';
import { Ic, type IconName } from './Icon';
import { TABS, type Tab } from './Shell';

interface Item { i: IconName; l: string; s: string; f(): void }

interface Props { open: boolean; onClose(): void; orders: OpsOrder[]; src: Source; onTab(t: Tab): void; onOrder(id: string): void; onHome(id: string): void; onAsk(q: string): void }

export function Palette({ open, onClose, orders, src, onTab, onOrder, onHome, onAsk }: Props) {
  const [q, setQ] = useState('');
  const [homes, setHomes] = useState<HomeRow[]>([]);
  useEffect(() => { if (open) src.homes().then(setHomes).catch(() => {}); }, [open, src]);
  const [ki, setKi] = useState(0);
  const inp = useRef<HTMLInputElement>(null);
  useEffect(() => { if (open) { setQ(''); setKi(0); setTimeout(() => inp.current?.focus(), 20); } }, [open]);

  const groups = useMemo(() => {
    const qq = q.toLowerCase().trim();
    const G: [string, Item[]][] = [];
    G.push([S.k.go, TABS.filter(([k]) => !qq || S.tabs[k].toLowerCase().includes(qq)).map(([k, i]) => ({ i, l: S.tabs[k], s: '', f: () => onTab(k) }))]);
    G.push([S.k.orders, orders.filter((o) => !qq || (o.ref + o.customer.name + o.customer.quartier).toLowerCase().includes(qq)).slice(0, 5)
      .map((o) => ({ i: 'list' as IconName, l: `${o.ref} · ${o.customer.name}`, s: statusPill(o.status)[0], f: () => onOrder(o.id) }))]);
    G.push([S.k.homes, homes.filter((h) => !qq || (h.name + h.quartier + (h.phone ?? '')).toLowerCase().includes(qq)).slice(0, 5)
      .map((h) => ({ i: 'house' as IconName, l: h.name, s: h.quartier + (h.home?.level != null ? ` · ${h.home.level} %` : ''), f: () => onHome(h.customer_id) }))]);
    if (qq) G.push(['Gazelle', [{ i: 'bolt', l: `${S.k.ask} : « ${q} »`, s: '', f: () => onAsk(q) }]]);
    return G.filter((g) => g[1].length);
  }, [q, orders, homes, onTab, onOrder, onHome, onAsk]);
  const flat = groups.flatMap((g) => g[1]);
  const run = (it?: Item) => { if (!it) return; onClose(); it.f(); };

  if (!open) return null;
  return (
    <div className="kbar on" role="dialog" aria-label={S.search} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="kbox">
        <div className="kin"><Ic n="search" s="s" />
          <input ref={inp} value={q} placeholder={S.k.placeholder} autoComplete="off"
            onChange={(e) => { setQ(e.target.value); setKi(0); }}
            onKeyDown={(e) => {
              if (e.key === 'Escape') onClose();
              if (e.key === 'ArrowDown') { setKi((k) => Math.min(flat.length - 1, k + 1)); e.preventDefault(); }
              if (e.key === 'ArrowUp') { setKi((k) => Math.max(0, k - 1)); e.preventDefault(); }
              if (e.key === 'Enter') run(flat[ki]);
            }} />
          <kbd className="mono" style={{ color: 'var(--ink-3)' }}>esc</kbd>
        </div>
        <div className="kres">
          {groups.length ? groups.map(([t, items]) => (
            <div key={t}>
              <div className="kgrp">{t}</div>
              {items.map((x) => { const k = flat.indexOf(x); return (
                <button key={t + x.l} className={'kit' + (k === ki ? ' on' : '')} onClick={() => run(x)}><Ic n={x.i} s="s" />{x.l}<span>{x.s}</span></button>
              ); })}
            </div>
          )) : <div className="empty"><b>{S.k.none}</b></div>}
        </div>
      </div>
    </div>
  );
}
